import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import {
  useWsmKitConfigDeleteMutation,
  useWsmKitConfigDetailsQuery,
  useWsmKitConfigUpdateMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { getMutationState } from "@dashboard/misc";
import { Skeleton } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import WsmKitDetailsPage from "../components/WsmKitDetailsPage/WsmKitDetailsPage";
import { sectionMessages } from "../messages";
import { wsmKitListPath } from "../urls";
import { getKitUpdateInput, kitToFormData } from "../utils";

export const WsmKitDetailsView = ({ id }: { id: string }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { data, loading } = useWsmKitConfigDetailsQuery({ variables: { id } });
  const [updateKit, updateKitOpts] = useWsmKitConfigUpdateMutation({
    onCompleted: result => {
      if (!result.wsmKitConfigUpdate?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
      }
    },
  });
  const [deleteKit, deleteKitOpts] = useWsmKitConfigDeleteMutation({
    onCompleted: result => {
      if (!result.wsmKitConfigDelete?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        navigate(wsmKitListPath);
      }
    },
  });
  const kit = data?.wsmKitConfig;

  if (loading || !kit) {
    return (
      <DetailPageLayout>
        <TopNav href={wsmKitListPath} title={intl.formatMessage(sectionMessages.sectionTitle)} />
        <DetailPageLayout.Content>
          <Skeleton />
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  const errors = updateKitOpts.data?.wsmKitConfigUpdate?.errors ?? [];

  return (
    <>
      <WsmKitDetailsPage
        key={kit.id}
        title={kit.collection?.name ?? intl.formatMessage(sectionMessages.sectionTitle)}
        collectionName={kit.collection?.name}
        currencyCode={kit.currencyCode}
        initialData={kitToFormData(kit)}
        errors={errors}
        disabled={updateKitOpts.loading}
        saveButtonBarState={getMutationState(
          updateKitOpts.called,
          updateKitOpts.loading,
          errors,
        )}
        onSubmit={formData =>
          updateKit({ variables: { id: kit.id, input: getKitUpdateInput(formData) } })
        }
        onDelete={() => setDeleteOpen(true)}
      />
      <ActionDialog
        open={deleteOpen}
        variant="delete"
        title={intl.formatMessage(sectionMessages.kitDeleteTitle)}
        confirmButtonState={getMutationState(deleteKitOpts.called, deleteKitOpts.loading, [])}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteKit({ variables: { id: kit.id } })}
      >
        {intl.formatMessage(sectionMessages.kitDeleteContent, {
          name: kit.collection?.name ?? "",
        })}
      </ActionDialog>
    </>
  );
};

export default WsmKitDetailsView;
